// Local message type to avoid circular dependency with app.types.ts
// This interface is compatible with MessageDTO but doesn't import it directly
export interface CompletionMessage {
  // Required fields from database
  message_id: string;
  role: string;
  content: string;

  // Optional fields from database (all nullable in database)
  conversation_id?: string | null;
  created_at?: string | null;
  edited_at?: string | null;
  user_id?: string | null;
  workspace_id?: string | null;
  model_id?: string | null;
  parent_message_id?: string | null;
  reply_to_message_id?: string | null;
  position?: number | null;
  status?: string | null;
  delivery_status?: string | null;
  is_edited?: boolean | null;

  // Token-related fields
  tokens?: number | null;
  prompt_tokens?: number | null;
  completion_tokens?: number | null;
  total_tokens?: number | null;
  cached_tokens?: number | null;

  // File-related fields
  file_id?: string | null;
  file_name?: string | null;
  file_size?: number | null;
  file_external_ref?: string | null;

  // JSON fields
  metadata?: any | null; // Json type from database
  attachment_metadata?: any | null; // Json type from database
  reactions?: any | null; // Json type from database

  // Extended fields from MessageDTO
  uploaded_file?: any; // FileUpload type
  file_attachments?: any[]; // FileUpload[] type

  // For tool calls (additional fields for AI providers)
  tool_calls?: any[];

  // Flexible for any additional fields
  [key: string]: any;
}

// Universal Tool Types (Provider-Agnostic)
export interface UniversalTool {
  name: string;
  description: string;
  parameters: {
    type: 'object';
    properties: Record<string, any>;
    required?: string[];
  };
  provider_metadata?: Record<string, any>;
}

// Provider-Specific Tool Types
export interface OpenAITool {
  type: 'function';
  function: {
    name: string;
    description: string;
    parameters: {
      type: 'object';
      properties: Record<string, any>;
      required?: string[];
    };
    strict?: boolean; // For structured outputs
  };
}

export interface GeminiTool {
  function_declarations: [
    {
      name: string;
      description: string;
      parameters: {
        type: 'object';
        properties: Record<string, any>;
        required?: string[];
      };
    },
  ];
}

export interface ClaudeTool {
  name: string;
  description: string;
  input_schema: {
    type: 'object';
    properties: Record<string, any>;
    required?: string[];
  };
}

// Backward compatibility
export type Tool = OpenAITool;

// Universal Tool Call Types (Provider-Agnostic)
export interface UniversalToolCall {
  id: string;
  name: string;
  arguments: Record<string, any> | string; // Parsed object or JSON string
  provider_metadata?: Record<string, any>;
}

export interface UniversalToolMessage {
  role: 'tool';
  content: string;
  tool_call_id: string;
  name?: string;
  provider_metadata?: Record<string, any>;
}

// Provider-Specific Tool Call Types
export interface OpenAIToolCall {
  id: string;
  type: 'function';
  function: {
    name: string;
    arguments: string; // JSON string
  };
}

export interface OpenAIToolMessage {
  role: 'tool';
  content: string;
  tool_call_id: string;
}

export interface GeminiToolCall {
  function_call: {
    name: string;
    args: Record<string, any>;
  };
}

export interface ClaudeToolCall {
  type: 'tool_use';
  id: string;
  name: string;
  input: Record<string, any>;
}

// Backward compatibility
export type ToolCall = OpenAIToolCall;
export type ToolMessage = OpenAIToolMessage;

export interface CompletionFileAttachment {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  url: string;
  content?: string;
  extractedText?: string;
}

export interface MessageContext {
  role: 'user' | 'assistant' | 'system';
  content: string;
  attachments?: CompletionFileAttachment[];
  timestamp?: Date;
  id?: string;
}

export interface CompletionRequest {
  model: string;
  messages: Partial<CompletionMessage>[];
  stream: boolean;
  workspaceId: string;
  temperature?: number;
  maxTokens?: number;
  topP?: number;
  frequencyPenalty?: number;
  presencePenalty?: number;
  stop?: string[];
  seed?: number;
  responseFormat?: {
    type: 'text' | 'json_object';
  };

  // Additional fields for unified backend logic
  chatType?: 'standard' | 'assistant';
  assistantId?: string;
  threadId?: string;
  instruction?: string;

  // File handling (preserve currentUserMessage logic for legacy support)
  currentUserMessage?: Partial<CompletionMessage>;

  // Modern tools API
  enableTools?: boolean; // Enable/disable tools
  tools?: UniversalTool[]; // Provider-agnostic tools
  tool_choice?: 'none' | 'auto' | 'required' | { type: 'function'; function: { name: string } };

  // Client context
  userId?: string;
  signal?: AbortSignal;

  // Assistant context (for backend logic)
  assistant?: {
    assistant_id: string;
    external_id: string | null;
    system_instructions?: string;
    settings?: {
      available_tools?: string[];
      [key: string]: any;
    };
  } | null;

  // Conversation context (for thread detection)
  conversation?: {
    conversationId?: string;
    threadId?: string;
  } | null;
}

export interface StreamingChunk {
  id: string;
  type: 'content' | 'done' | 'error';
  content?: string;
  finishReason?: 'stop' | 'length' | 'content_filter' | 'tool_calls';
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
  error?: {
    message: string;
    code?: string;
    type?: string;
  };
}

export interface ProviderCapabilities {
  supportsVision: boolean;
  supportsStreaming: boolean;
  supportsSystemMessages: boolean;
  supportsFunctionCalling: boolean;
  maxContextWindow: number;
  supportedImageFormats?: string[];
  maxImageSize?: number;
}

export interface ICompletionProvider {
  name: string;
  createCompletion(request: CompletionRequest): Promise<AsyncIterable<StreamingChunk>>;
  validateRequest(request: CompletionRequest): ValidationResult;
  getCapabilities(): ProviderCapabilities;
  getSupportedModels(): string[];
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

export interface ValidationError {
  field: string;
  message: string;
  code: string;
}

export type ProviderType = 'openai' | 'gemini' | 'claude';

export interface ProviderConfig {
  type: ProviderType;
  apiKey: string;
  baseUrl?: string;
  organization?: string;
  models: string[];
}

// Tool Status Enum for consistency
export enum ToolStreamingStatus {
  IDLE = 'idle',
  STARTED = 'started',
  EXECUTING = 'executing',
  COMPLETED = 'completed',
  FAILED = 'failed'
}

// Tool Event Types for Streaming
export interface ToolEventMetadata {
  /** Name of the tool being called */
  toolName: string;
  
  /** Unique identifier for the tool call (chat API) */
  toolId?: string;
  
  /** Step identifier for assistant API */
  stepId?: string;
  
  /** Which API is being used */
  api: 'chat' | 'assistant';
  
  /** Current status of the tool execution */
  status: ToolStreamingStatus;
  
  /** Execution duration in milliseconds */
  duration?: number;
  
  /** Error message if tool failed */
  error?: string;
  
  /** Number of tools being executed (for tool_calls type) */
  count?: number;
  
  /** Number of results returned */
  results?: number;
}

/**
 * Stream chunk types used in streaming responses
 */
export const STREAM_CHUNK_TYPES = {
  TEXT: 'text',
  ERROR: 'error',
  FINAL: 'final',
  TOOL_USE: 'tool_use',
  TOOL_CALLS: 'tool_calls',
  TOOL_END: 'tool_end',
  FUNCTION_CALL: 'function_call' // Legacy
} as const;

export type StreamChunkType = typeof STREAM_CHUNK_TYPES[keyof typeof STREAM_CHUNK_TYPES];

// Tool Transformation Utilities
export class ToolTransformers {
  static toOpenAI(tool: UniversalTool): OpenAITool {
    return {
      type: 'function',
      function: {
        name: tool.name,
        description: tool.description,
        parameters: tool.parameters,
        strict: tool.provider_metadata?.['strict'] || false,
      },
    };
  }

  static toGemini(tool: UniversalTool): GeminiTool {
    return {
      function_declarations: [
        {
          name: tool.name,
          description: tool.description,
          parameters: tool.parameters,
        },
      ],
    };
  }

  static toClaude(tool: UniversalTool): ClaudeTool {
    return {
      name: tool.name,
      description: tool.description,
      input_schema: tool.parameters,
    };
  }

  static fromOpenAI(tool: OpenAITool): UniversalTool {
    return {
      name: tool.function.name,
      description: tool.function.description,
      parameters: tool.function.parameters,
      provider_metadata: { ['strict']: tool.function.strict },
    };
  }

  static fromGemini(tool: GeminiTool): UniversalTool {
    const func = tool.function_declarations[0];
    return {
      name: func.name,
      description: func.description,
      parameters: func.parameters,
    };
  }

  static fromClaude(tool: ClaudeTool): UniversalTool {
    return {
      name: tool.name,
      description: tool.description,
      parameters: tool.input_schema,
    };
  }
}
